/**
 * The five analyses, as one anchored tab bar.
 *
 * ANCHORED, NOT FLOATING. This started as a rounded pill container sitting in
 * the scroll content, which read as a card that happened to hold buttons —
 * detached from the header above it and from the report below it. It is now a
 * bar: the same surface as the header, its own hairline underneath, and an
 * underline marking the selected tab. It sits OUTSIDE the ScrollView, so the
 * report scrolls beneath it and the reader never loses their place in the set.
 *
 * IT FITS RATHER THAN SCROLLS. The labels are the short forms the approved
 * mobile mockups use, chosen so all five sit on a 390pt phone at once. The
 * full-width version had to auto-scroll the selected tab into view, driven off
 * layout measurements whose ordering React Native does not guarantee — and
 * when it lost that race a deep-linked "Exit Strategy" sat off the right edge
 * with "Competition" appearing to be the selection. Fitting removes the race
 * instead of trying to time it.
 *
 * Nothing is abbreviated away: the FULL analysis name is what the breadcrumb,
 * the section headings and the framework modal use, and it is what a screen
 * reader announces here — `accessibilityLabel` carries the long form.
 *
 * The row still scrolls as a fallback, for a narrow device or large text
 * settings, but no correctness depends on it.
 */

import React from 'react';
import { ScrollView, Text, View } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAnalysisVisuals } from './analysis-visuals';
import {
  ANALYSIS_LABELS,
  ANALYSIS_TABS,
  ANALYSIS_TAB_LABELS,
  type AnalysisType,
} from '@/lib/strategic-analysis/types';

export function AnalysisTabs({
  value,
  onChange,
}: {
  value: AnalysisType;
  onChange: (next: AnalysisType) => void;
}) {
  const v = useAnalysisVisuals();

  return (
    <View
      style={{
        backgroundColor: v.colors.surface.primary,
        borderBottomWidth: 1,
        borderBottomColor: v.colors.border.light,
      }}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        // A horizontal ScrollView expands vertically to fill a flex parent
        // unless it is told not to.
        style={{ flexGrow: 0 }}
        contentContainerStyle={{ paddingHorizontal: 8 }}
        accessibilityRole="tablist"
      >
        {ANALYSIS_TABS.map((analysis) => {
          const active = analysis === value;
          return (
            <AnimatedPressable
              key={analysis}
              onPress={() => onChange(analysis)}
              accessibilityRole="tab"
              accessibilityState={{ selected: active }}
              // The full name, so the strip reads as the five analyses rather
              // than as five abbreviations.
              accessibilityLabel={ANALYSIS_LABELS[analysis]}
              className="px-3 pt-3 items-center justify-end"
              style={{ minHeight: 46 }}
            >
              <Text
                numberOfLines={1}
                className="text-[14px]"
                style={{
                  color: active ? v.colors.primary[700] : v.colors.text.secondary,
                  fontWeight: active ? '700' : '500',
                }}
              >
                {ANALYSIS_TAB_LABELS[analysis]}
              </Text>
              {/* The underline sits ON the bar's own hairline, which is what
                  makes the tab read as part of the chrome rather than as a
                  chip floating above the content. */}
              <View
                style={{
                  alignSelf: 'stretch',
                  height: 2.5,
                  marginTop: 8,
                  borderTopLeftRadius: 2,
                  borderTopRightRadius: 2,
                  backgroundColor: active ? v.colors.primary[600] : 'transparent',
                }}
              />
            </AnimatedPressable>
          );
        })}
      </ScrollView>
    </View>
  );
}
